import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePeriodeStore } from "@/hooks/use-periode";
import { getMasterKonstanta } from "@/pages/common/remun-dosen/api/master-konstanta";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, BarChart3, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Symbols,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { getAnalytics70 } from "./api/analytics-70";

export default function DistributionPage() {
  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester, periodeId } = getYearAndSemester();

  const targetYear = semester === 2 ? year + 1 : year;
  const [selectedMonth, setSelectedMonth] = useState(`${targetYear}_01`);

  const { data: konstantaData = [] } = useQuery({
    queryKey: ["master-konstanta", year, semester],
    queryFn: () => getMasterKonstanta(year, semester),
    enabled: !!year && !!semester,
  });

  const { data: analyticsData = [], isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ["analytics-70", year, semester, periodeId, selectedMonth],
    queryFn: () =>
      getAnalytics70(year, semester, periodeId || 1, selectedMonth),
    enabled: (!!year && !!semester && !!periodeId) || !!selectedMonth,
  });

  console.log({ year, semester, periodeId, selectedMonth, analyticsData });

  const groupedKonstanta = useMemo(() => {
    const groups = {} as Record<number, string[]>;
    konstantaData.forEach((item) => {
      if (!groups[item.konstanta]) groups[item.konstanta] = [];
      groups[item.konstanta].push(item.fungsional);
    });
    return Object.entries(groups).map(([k, fungsionals]) => ({
      konstanta: Number(k),
      label: `${k} SKS (Target ${fungsionals.join(", ")})`,
    }));
  }, [konstantaData]);

  const {
    chartData,
    uniqueJabatans,
    zeroRp70Count,
    sortedStats,
    uniqueFakultas,
    fakultasShapeMap,
    over30Stats,
    zeroRp70Stats,
    totalDosen,
  } = useMemo(() => {
    let uniqueJabs = Array.from(
      new Set(analyticsData.map((d: any) => d.Fungsional || "Unknown")),
    );

    const order = [
      "Tenaga Pengajar",
      "Asisten Ahli",
      "Lektor",
      "Lektor Kepala",
      "Guru Besar",
    ];
    uniqueJabs.sort((a, b) => {
      let indexA = order.findIndex((o) => a.includes(o));
      let indexB = order.findIndex((o) => b.includes(o));
      if (indexA === -1) indexA = 99;
      if (indexB === -1) indexB = 99;
      return indexA - indexB;
    });

    let zeroCount = 0;

    const dataWithJitter = analyticsData.map((d: any) => {
      const rp70 = parseFloat(d.RupiahBersih70) || 0;
      if (rp70 === 0) zeroCount++;

      const fungsional = d.Fungsional || "Unknown";
      const jabatanIndex = uniqueJabs.indexOf(fungsional);
      const jitter = (Math.random() - 0.5) * 0.4;
      return {
        ...d,
        jabatan: fungsional,
        fakultas: d.Fakultas || "Unknown",
        nama: d.NamaDosen,
        sks: parseFloat(d.SKSSebelumDikurang) || 0,
        rp70: rp70,
        xValue: jabatanIndex + jitter,
        jabatanIndex,
      };
    });

    const calcMedian = (arr: number[]) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const half = Math.floor(sorted.length / 2);
      if (sorted.length % 2 === 0)
        return (sorted[half - 1] + sorted[half]) / 2.0;
      return sorted[half];
    };
    const calcP75 = (arr: number[]) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = Math.floor(sorted.length * 0.75);
      return sorted[index];
    };

    const sortedSksStats = uniqueJabs.map((jabatan) => {
      const jData = dataWithJitter.filter((d: any) => d.jabatan === jabatan);
      const sksArr = jData.map((d: any) => d.sks);

      const median = calcMedian(sksArr);
      const mean = sksArr.length
        ? sksArr.reduce((a, b) => a + b, 0) / sksArr.length
        : 0;
      const meanHalf = mean / 2;
      const p75 = calcP75(sksArr);
      const min = Math.min(...(sksArr.length ? sksArr : [0]));
      const max = Math.max(...(sksArr.length ? sksArr : [0]));

      const kData = konstantaData.find(
        (k) => k.fungsional.includes(jabatan) || jabatan.includes(k.fungsional),
      );
      const fallbackK = parseFloat(jData[0]?.KonstantaFungsional) || 30;
      const currentKonstanta = kData ? kData.konstanta : fallbackK;

      return {
        jabatan,
        median,
        mean,
        meanHalf,
        p75,
        min,
        max,
        currentKonstanta,
      };
    });

    const uniqueFakultasArr = Array.from(
      new Set(dataWithJitter.map((d: any) => d.fakultas)),
    );
    const SHAPES = [
      "circle",
      "cross",
      "diamond",
      "square",
      "star",
      "triangle",
      "wye",
    ];
    const fMap = uniqueFakultasArr.reduce((acc: any, f: any, i: number) => {
      acc[f] = SHAPES[i % SHAPES.length];
      return acc;
    }, {});

    const over30Stats = uniqueJabs.map((jabatan: any) => {
      const total = dataWithJitter.filter(
        (d: any) => d.jabatan === jabatan,
      ).length;
      const over30 = dataWithJitter.filter(
        (d: any) => d.jabatan === jabatan && d.sks >= 30,
      ).length;
      const percentage = total > 0 ? Math.round((over30 / total) * 100) : 0;
      return { jabatan, total, over30, percentage };
    });

    const zeroRp70Stats = uniqueJabs.map((jabatan: any) => {
      const total = dataWithJitter.filter(
        (d: any) => d.jabatan === jabatan,
      ).length;
      const zeroRp = dataWithJitter.filter(
        (d: any) => d.jabatan === jabatan && d.rp70 === 0,
      ).length;
      const percentage = total > 0 ? Math.round((zeroRp / total) * 100) : 0;
      return { jabatan, total, zeroRp, percentage };
    });

    return {
      chartData: dataWithJitter,
      uniqueJabatans: uniqueJabs,
      zeroRp70Count: zeroCount,
      totalDosen: dataWithJitter.length,
      sortedStats: sortedSksStats,
      uniqueFakultas: uniqueFakultasArr,
      fakultasShapeMap: fMap,
      over30Stats,
      zeroRp70Stats,
    };
  }, [analyticsData, konstantaData]);

  const COLORS = [
    "#3b82f6",
    "#ef4444",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
  ];

  const formatRupiah = (value: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderFakultasShape = (props: any) => {
    const { cx, cy, fill, payload } = props;
    const shapeType = fakultasShapeMap[payload.fakultas] || "circle";
    return (
      <Symbols
        cx={cx}
        cy={cy}
        type={shapeType as any}
        size={60}
        fill={fill}
        fillOpacity={0.7}
      />
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-muted/30">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Distribusi & Clusterisasi
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Sebaran data SKS dan nominal Remunerasi 70% Kotor per Jabatan
            Fungsional
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-3">
          <Link to="/admin/dashboard/analytics-70-table">
            <Button
              variant="outline"
              className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 transition-colors"
            >
              Lihat Data Tabel
            </Button>
          </Link>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-45">
              <SelectValue placeholder="Pilih Bulan" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                const targetYear = semester === 2 ? year + 1 : year;
                const val = `${targetYear}_${m.toString().padStart(2, "0")}`;
                return (
                  <SelectItem key={val} value={val}>
                    Bulan {m} ({targetYear})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button variant="outline" asChild>
            <Link to="/admin/dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Kembali ke Dashboard
            </Link>
          </Button>
        </div>
      </div>

      {isLoadingAnalytics && (
        <div className="flex items-center justify-center p-12 w-full h-64 bg-slate-50 rounded-xl border border-slate-100">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span>Memuat data analitik...</span>
          </div>
        </div>
      )}

      {!isLoadingAnalytics && chartData.length === 0 && (
        <div className="flex items-center justify-center p-12 w-full h-64 bg-slate-50 rounded-xl border border-slate-100 text-muted-foreground">
          Belum ada data untuk periode dan bulan yang dipilih.
        </div>
      )}

      {!isLoadingAnalytics && chartData.length > 0 && (
        <div className="space-y-6">
          {/* Ringkasan Insight */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-red-50/50 border-red-100 shadow-sm relative overflow-hidden">
              <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none">
                <AlertCircle className="w-32 h-32 text-red-600" />
              </div>
              <CardContent className="p-6 relative z-10">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-red-600/80">
                    Perhatian
                  </span>
                  <span className="text-3xl font-bold text-slate-800">
                    {zeroRp70Count}
                    <span className="text-base font-normal text-slate-500 ml-2">
                      Dosen
                    </span>
                  </span>
                  <span className="text-sm font-medium text-slate-600 mt-1">
                    tidak mendapatkan Rupiah 70% (Rp 0)
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50/50 border-blue-100 shadow-sm relative overflow-hidden">
              <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none">
                <BarChart3 className="w-32 h-32 text-blue-600" />
              </div>
              <CardContent className="p-6 relative z-10">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-blue-600/80">
                    Total Partisipan
                  </span>
                  <span className="text-3xl font-bold text-slate-800">
                    {totalDosen}
                    <span className="text-base font-normal text-slate-500 ml-2">
                      Dosen
                    </span>
                  </span>
                  <span className="text-sm font-medium text-slate-600 mt-1">
                    telah memiliki perhitungan SKS
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chart 1: SKS vs Fungsional (Scatter Plot dengan Jitter) */}
          <Card className="rounded-xl shadow-sm border-muted/40 overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
              <CardTitle className="text-lg font-bold text-slate-800">
                Clusterisasi Total SKS
              </CardTitle>
              <CardDescription>
                Distribusi total SKS dosen berdasarkan Jabatan Fungsional.
                Bentuk mewakili Fakultas.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4 mb-4 text-xs font-medium text-slate-600">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-0.5 bg-red-500 border-t-2 border-red-500 border-dashed"></div>
                  <span>Target Minimal Konstanta</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-0.5 bg-emerald-500 border-t-2 border-emerald-500 border-dashed"></div>
                  <span>Nilai Median</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-0.5 bg-blue-500"></div>
                  <span>Nilai Rata-rata (Mean)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-0.5 border-t-2 border-orange-500 border-dotted"></div>
                  <span>Nilai Mean / 2</span>
                </div>
              </div>
              <div className="h-300 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    {groupedKonstanta.map((item) => (
                      <ReferenceLine
                        key={item.konstanta}
                        y={item.konstanta}
                        stroke="#ef4444"
                        strokeDasharray="3 3"
                        strokeWidth={2}
                        label={{
                          position: "top",
                          value: item.label,
                          fill: "#ef4444",
                          fontSize: 12,
                          fontWeight: "bold",
                        }}
                      />
                    ))}
                    {/* Garis Median Menyambung */}
                    <Scatter
                      name="Median Line"
                      data={sortedStats.map((stat: any) => ({
                        xValue: uniqueJabatans.indexOf(stat.jabatan),
                        sks: stat.median,
                        jabatan: stat.jabatan,
                        isMedian: true,
                      }))}
                      line={{
                        stroke: "#10b981",
                        strokeWidth: 2,
                        strokeDasharray: "4 4",
                      }}
                      shape={<circle r={0} />}
                      fill="none"
                      isAnimationActive={false}
                    />

                    {/* Garis Rata-rata (Mean) Menyambung */}
                    <Scatter
                      name="Mean Line"
                      data={sortedStats.map((stat: any) => ({
                        xValue: uniqueJabatans.indexOf(stat.jabatan),
                        sks: stat.mean,
                        jabatan: stat.jabatan,
                        isMean: true,
                      }))}
                      line={{ stroke: "#3b82f6", strokeWidth: 2 }}
                      shape={<circle r={0} />}
                      fill="none"
                      isAnimationActive={false}
                    />

                    {/* Garis Mean / 2 Menyambung */}
                    <Scatter
                      name="Mean Half Line"
                      data={sortedStats.map((stat: any) => ({
                        xValue: uniqueJabatans.indexOf(stat.jabatan),
                        sks: stat.meanHalf,
                        jabatan: stat.jabatan,
                        isMeanHalf: true,
                      }))}
                      line={{
                        stroke: "#f97316",
                        strokeWidth: 2,
                        strokeDasharray: "2 2",
                      }}
                      shape={<circle r={0} />}
                      fill="none"
                      isAnimationActive={false}
                    />
                    <XAxis
                      dataKey="xValue"
                      type="number"
                      name="Jabatan Fungsional"
                      domain={[-0.5, uniqueJabatans.length - 0.5]}
                      ticks={uniqueJabatans.map((_, i) => i)}
                      tickFormatter={(val) =>
                        uniqueJabatans[val as number] || ""
                      }
                      tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
                      tickMargin={10}
                      axisLine={{ stroke: "#cbd5e1" }}
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      dataKey="sks"
                      type="number"
                      name="Total SKS"
                      tick={{ fill: "#64748b", fontSize: 12 }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                    />
                    <ZAxis dataKey="sks" range={[40, 40]} name="SKS" />
                    <Tooltip
                      cursor={{ strokeDasharray: "3 3", stroke: "#cbd5e1" }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        boxShadow:
                          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                        fontWeight: 500,
                      }}
                      formatter={(value: any, name: any, props: any) => {
                        if (name === "Total SKS")
                          return [
                            `${value} SKS`,
                            `Dosen: ${props.payload.nama} (${props.payload.fakultas})`,
                          ];
                        if (name === "Jabatan Fungsional")
                          return [props.payload.jabatan, "Jabatan"];
                        return [value, name];
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: "20px" }}
                      iconType="circle"
                    />
                    {uniqueJabatans.map((jabatan: any, index: number) => (
                      <Scatter
                        key={jabatan}
                        name={jabatan}
                        data={chartData.filter(
                          (d: any) => d.jabatan === jabatan,
                        )}
                        fill={COLORS[index % COLORS.length]}
                        shape={renderFakultasShape}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 pt-4 border-t border-slate-100">
                {uniqueFakultas.map((f: any) => (
                  <div
                    key={f}
                    className="flex items-center gap-2 text-xs font-medium text-slate-600"
                  >
                    <svg width="14" height="14" viewBox="0 0 32 32">
                      <Symbols
                        cx={16}
                        cy={16}
                        type={fakultasShapeMap[f] as any}
                        size={150}
                        fill="#64748b"
                      />
                    </svg>
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-xl bg-slate-50/80 border border-slate-100">
                <h4 className="font-semibold text-slate-800 mb-3 text-sm flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  Pencapaian Target Minimum (30 SKS) per Jabatan
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {over30Stats.map((stat: any) => (
                    <div
                      key={stat.jabatan}
                      className="bg-white p-3 rounded-lg shadow-sm border border-slate-200/60 flex flex-col justify-between"
                    >
                      <div
                        className="text-xs text-slate-500 font-medium mb-1 truncate"
                        title={stat.jabatan}
                      >
                        {stat.jabatan}
                      </div>
                      <div>
                        <div className="text-xl font-bold text-slate-800 leading-none">
                          {stat.over30}{" "}
                          <span className="text-xs font-normal text-slate-400">
                            / {stat.total}
                          </span>
                        </div>
                        <div className="text-xs font-semibold mt-1 text-green-600 bg-green-50 w-fit px-1.5 py-0.5 rounded">
                          {stat.percentage}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chart 2: SKS vs Rupiah 70% Kotor */}
          <Card className="rounded-xl shadow-sm border-muted/40 overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
              <CardTitle className="text-lg font-bold text-slate-800">
                Sebaran Rupiah 70% Bersih
              </CardTitle>
              <CardDescription>
                Memetakan nilai Rupiah berdasarkan Jabatan Fungsional.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="w-full h-300">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <ReferenceLine
                      y={0}
                      stroke="#ef4444"
                      strokeDasharray="3 3"
                      strokeWidth={2}
                      label={{
                        position: "top",
                        value: "Rp 0 (Tidak mendapat Rupiah)",
                        fill: "#ef4444",
                        fontSize: 12,
                        fontWeight: "bold",
                      }}
                    />
                    <XAxis
                      dataKey="xValue"
                      type="number"
                      name="Jabatan Fungsional"
                      domain={[-0.5, uniqueJabatans.length - 0.5]}
                      ticks={uniqueJabatans.map((_, i) => i)}
                      tickFormatter={(val) =>
                        uniqueJabatans[val as number] || ""
                      }
                      tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
                      tickMargin={10}
                      axisLine={{ stroke: "#cbd5e1" }}
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      dataKey="rp70"
                      type="number"
                      name="Rupiah 70%"
                      tickFormatter={(val) => formatRupiah(val)}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      width={110}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                    />
                    <ZAxis dataKey="rp70" range={[40, 40]} name="Rp70" />
                    <Tooltip
                      cursor={{ strokeDasharray: "3 3", stroke: "#cbd5e1" }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        boxShadow:
                          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                        fontWeight: 500,
                      }}
                      formatter={(value: any, name: any, props: any) => {
                        if (name === "Rupiah 70%")
                          return [
                            formatRupiah(value),
                            `Dosen: ${props.payload.nama} (${props.payload.fakultas})`,
                          ];
                        if (name === "Jabatan Fungsional")
                          return [props.payload.jabatan, "Jabatan"];
                        return [value, name];
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: "20px" }}
                      iconType="circle"
                    />
                    {uniqueJabatans.map((jabatan: any, index: number) => (
                      <Scatter
                        key={jabatan}
                        name={jabatan}
                        data={chartData.filter(
                          (d: any) => d.jabatan === jabatan,
                        )}
                        fill={COLORS[index % COLORS.length]}
                        shape={renderFakultasShape}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-6 p-5 rounded-xl bg-slate-50/80 border border-slate-100">
                <h4 className="font-semibold text-slate-800 mb-3 text-sm flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  Dosen dengan Pencapaian Rupiah 70% = Rp 0 per Jabatan
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {zeroRp70Stats.map((stat: any) => (
                    <div
                      key={stat.jabatan}
                      className="bg-white p-3 rounded-lg shadow-sm border border-slate-200/60 flex flex-col justify-between"
                    >
                      <div
                        className="text-xs text-slate-500 font-medium mb-1 truncate"
                        title={stat.jabatan}
                      >
                        {stat.jabatan}
                      </div>
                      <div>
                        <div className="text-xl font-bold text-slate-800 leading-none">
                          {stat.zeroRp}{" "}
                          <span className="text-xs font-normal text-slate-400">
                            / {stat.total}
                          </span>
                        </div>
                        <div className="text-xs font-semibold mt-1 text-red-600 bg-red-50 w-fit px-1.5 py-0.5 rounded">
                          {stat.percentage}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chart 3: Analisis Konstanta (Target SKS Wajar) */}
          <Card className="rounded-xl shadow-sm border-muted/40 overflow-hidden">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4">
              <CardTitle className="text-lg font-bold text-slate-800">
                Analisis Target SKS (Konstanta Ideal)
              </CardTitle>
              <CardDescription>
                Membandingkan Median dan Persentil-75 SKS Aktual dengan Target
                KONSTANTA dari Master BKD.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-100 w-full" style={{ height: "400px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={sortedStats}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="jabatan"
                      tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
                      tickMargin={10}
                      axisLine={{ stroke: "#cbd5e1" }}
                    />
                    <YAxis
                      tick={{ fill: "#64748b", fontSize: 12 }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                        boxShadow:
                          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                        fontWeight: 500,
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: "20px" }} />
                    <Bar
                      dataKey="median"
                      name="Median SKS Aktual"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={60}
                    />
                    <Bar
                      dataKey="p75"
                      name="Persentil-75 SKS Aktual"
                      fill="#93c5fd"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={60}
                    />
                    <Line
                      type="monotone"
                      dataKey="currentKonstanta"
                      name="Target KONSTANTA (Master)"
                      stroke="#ef4444"
                      strokeWidth={3}
                      dot={{
                        r: 6,
                        fill: "#ef4444",
                        strokeWidth: 2,
                        stroke: "#fff",
                      }}
                      activeDot={{ r: 8 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-8 p-5 bg-blue-50/60 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  <h4 className="font-bold text-blue-900 text-sm">
                    Interpretasi Distribusi (Contoh)
                  </h4>
                </div>
                <ul className="list-disc pl-5 text-sm text-blue-800 space-y-1.5 opacity-90 leading-relaxed">
                  <li>
                    Untuk <strong>Tenaga Pengajar</strong>, Target Konstanta
                    (64.5) jauh di atas Persentil ke-75 SKS aktual (14.65 SKS).
                    Artinya, mayoritas dosen jauh di bawah target tersebut.
                  </li>
                  <li>
                    Untuk <strong>Lektor</strong>, Target Konstanta (66.5) juga
                    sulit tercapai karena Persentil ke-75 berada di kisaran 36
                    SKS.
                  </li>
                  <li>
                    Untuk <strong>Guru Besar</strong>, target Konstanta (70.5)
                    sudah mudah terlampaui karena titik tengah (Median) aktual
                    dosen mencapai 84.4 SKS.
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
